import { isEmpty, isEqual } from 'lodash';
import type { Dispatch } from 'redux';

import { getSubject, getUserAccount, isUserSessionExpired } from '../../configuration/login/selectors';
import type { State } from '../../reducers';
import { fetchAssets, fetchDrivers, fetchProductActivations, fetchTags } from '../api/api';
import { apiCallActions } from '../api/redux/api.redux';
import { ApiCalls, ErrorCauses } from '../api/redux/types';
import { getDetailsSidebarDisplayItems, realtimeSidebarActions } from '../detailssidebar/redux/detailsSidebar.redux';
import {
    getColumnOrder,
    getSortBy,
    tableSettingsActions,
} from '../realtime/header/tablesettings/redux/tablesettings.redux';
import { getDrivingTimeDisplayItems, realtimeActions } from '../realtime/redux/realtime.redux';
import { getDefaultSortBy, getUpdatedCurrentColumnOrder } from '../tables/drivingtimes/columnService';
import { separateTrucksIntoGroups } from './groupingService';
import { dataActions } from './redux/data.redux';
import type { Asset, AssetBookings, AssetItem, Driver, ProductActivations, Tag } from './redux/types';
import {
    assignOrphanDriversToDefaultGroup,
    getDriverGroups,
    removeRestrictedDriverTags,
} from './service/driverService';
import { addDriverAndAssetInfoToDetailsSidebarDisplayItem } from './service/updateDetailsSidebarDisplayItemsService';
import { addDriverAndAssetInfoToDisplayItem } from './service/updateDrivingTimesDisplayItemsService';

const updateDrivingDisplayItems = (
    state: State,
    dispatch: Dispatch,
    vehicles: AssetItem[],
    drivers: Driver[]
): void => {
    const drivingTimeDisplayItems = getDrivingTimeDisplayItems(state);
    if (!isEmpty(drivingTimeDisplayItems)) {
        const enrichedDrivingTimesDisplayItems = addDriverAndAssetInfoToDisplayItem(
            drivingTimeDisplayItems,
            vehicles,
            drivers
        );
        dispatch(realtimeActions.fetchedDrivingTimesDisplayItems(enrichedDrivingTimesDisplayItems));
    }
};

const updateDetailsSidebarDisplayItems = (
    state: State,
    dispatch: Dispatch,
    vehicles: AssetItem[],
    drivers: Driver[]
): void => {
    const detailsSidebarDisplayItems = getDetailsSidebarDisplayItems(state);
    if (!isEmpty(detailsSidebarDisplayItems)) {
        const enrichedDetailsSidebarDisplayItems = addDriverAndAssetInfoToDetailsSidebarDisplayItem(
            detailsSidebarDisplayItems,
            vehicles,
            drivers
        );
        dispatch(realtimeSidebarActions.fetchedDetailsSidebarDisplayItems(enrichedDetailsSidebarDisplayItems));
    }
};

const logWarning = (getState: () => State, message: string) => {
    const subject = getSubject(getState());
    const userAccount = getUserAccount(getState());
    console.warn(`${message} for subject ${subject} in account ${userAccount}`);
};

export const fetchDataThunk = (dispatch: Dispatch, getState: () => State): Promise<void> => {
    if (isUserSessionExpired(getState())) {
        dispatch(apiCallActions.setErrorForApiCall({ type: ErrorCauses.SESSION_EXPIRED_FETCH, value: true }));
        return Promise.resolve();
    }

    const requests = [fetchAssets(), fetchTags(), fetchDrivers(), fetchProductActivations()] as Promise<
        Tag[] | Asset[] | Driver[] | undefined | AssetBookings
    >[];

    dispatch(apiCallActions.setLoadingForApiCall({ type: ApiCalls.ASSET_TREE, value: true }));
    dispatch(apiCallActions.setErrorForApiCall({ type: ErrorCauses.ASSET_TREE_FETCH, value: false }));

    return Promise.all(requests)
        .then(result => {
            const assets = result[0] as Asset[];
            const tags = result[1] as Tag[];
            const drivers = assignOrphanDriversToDefaultGroup(removeRestrictedDriverTags(tags, result[2] as Driver[]));
            const productActivations = result[3] as ProductActivations | undefined;

            const groupedAssets = separateTrucksIntoGroups(assets, tags);

            updateDrivingDisplayItems(getState(), dispatch, groupedAssets.trucks, drivers);
            updateDetailsSidebarDisplayItems(getState(), dispatch, groupedAssets.trucks, drivers);
            dispatch(dataActions.fetchedVehicleGroups(groupedAssets.truckGroups));
            dispatch(dataActions.fetchedTags(tags));

            dispatch(dataActions.setAssetBookings(productActivations?.assetBookings));
            dispatch(dataActions.setFeatures(productActivations?.features || []));
            const currentColumnOrderInState = getColumnOrder(getState());
            const currentSortByInState = getSortBy(getState());
            const updatedCurrentColumnOrder = getUpdatedCurrentColumnOrder(getState());

            if (!isEqual(currentColumnOrderInState, updatedCurrentColumnOrder)) {
                dispatch(tableSettingsActions.setColumnOrder(updatedCurrentColumnOrder));
            }
            if (!updatedCurrentColumnOrder.includes(currentSortByInState)) {
                dispatch(tableSettingsActions.setSortBy(getDefaultSortBy()));
            }
            dispatch(dataActions.fetchedDrivers(drivers));
            dispatch(dataActions.fetchedDriverGroups(getDriverGroups(tags, drivers)));
            dispatch(dataActions.fetchedAssets(groupedAssets.trucks));
            dispatch(apiCallActions.setLoadingForApiCall({ type: ApiCalls.ASSET_TREE, value: false }));
        })
        .catch((error?: Error) => {
            if (error?.message?.includes('UNAUTHENTICATED')) {
                logWarning(getState, error?.message);
            } else if (error?.message?.includes('ACCESS_DENIED')) {
                logWarning(getState, error?.message);
                dispatch(
                    apiCallActions.setErrorForApiCall({
                        type: ErrorCauses.ACCESS_DENIED_FETCH,
                        value: true,
                    })
                );
            } else {
                console.error(error);
            }
            dispatch(apiCallActions.setErrorForApiCall({ type: ErrorCauses.ASSET_TREE_FETCH, value: true }));
            dispatch(apiCallActions.setLoadingForApiCall({ type: ApiCalls.ASSET_TREE, value: false }));
        });
};
